import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/shared/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Badge } from "@/shared/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/shared/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { User, Plus, Edit, Crown, Upload, ArrowLeft } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEmail } from "@/shared/hooks/useEmail";
import { handleCheckout } from "@/shared/utils/checkoutHandler";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { PhoneInput } from "@/shared/components/ui/phone-input";
import { apiClient } from "@/shared/services/apiClient";

interface UserData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: "admin" | "user";
  joinDate: string;
  status: "active" | "pending";
  avatar?: string;
}

const BASE_FREE_USERS = 1;

const UsersPage = () => {
  const { toast } = useToast();
  const { sendEmail, isLoading: isEmailLoading } = useEmail();
  const { refreshSubscription } = useSubscription();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const checkoutCleanupRef = useRef<(() => void) | null>(null);
  const [users, setUsers] = useState<UserData[]>([
    {
      id: "1",
      name: "Current Admin", // This represents the account owner
      email: "admin@company.com", // Would be actual logged-in user email
      role: "admin",
      joinDate: "2024-01-15",
      status: "active",
      avatar: undefined
    }
    // Start with just the admin user - additional slots shown as available
  ]);

  const [showUpgradeWarning, setShowUpgradeWarning] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
    role: "user" as "admin" | "user"
  });
  const [editingUserCountryCode, setEditingUserCountryCode] = useState("+1");

  const getCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        // Update the admin user with real data
        setUsers(prev => prev.map(u => 
          u.role === 'admin' ? {
            ...u,
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Admin User',
            email: user.email || 'admin@company.com',
            phone: user.user_metadata?.phone_number || '',
            joinDate: new Date(user.created_at).toISOString().split('T')[0]
          } : u
        ));
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      setLoading(true);
      console.log('Checking subscription...');
      const data = await apiClient.get('/edge/check-subscription');
      console.log('Subscription data received:', data);
      setSubscriptionData(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Set default data on error to prevent blank display
      setSubscriptionData({
        subscribed: false,
        subscription_tier: null,
        user_count: BASE_FREE_USERS,
        trial_active: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCurrentUser();
    checkSubscription();
  }, []);

  // Cleanup checkout handler on unmount
  useEffect(() => {
    return () => {
      if (checkoutCleanupRef.current) {
        checkoutCleanupRef.current();
        checkoutCleanupRef.current = null;
      }
    };
  }, []);

  const handleAddUser = () => {
    const currentUserCount = users.length;
    const maxFreeUsers = BASE_FREE_USERS;
    
    // If they have less than 2 users total, allow free addition
    if (currentUserCount < maxFreeUsers) {
      setShowAddUserDialog(true);
    } 
    // If they're at the limit and don't have a subscription, show upgrade warning
    else if (!subscriptionData?.subscribed) {
      setShowUpgradeWarning(true);
    }
    // If they're subscribed but at their plan limit, show upgrade warning  
    else if (currentUserCount >= (subscriptionData.user_count || 2)) {
      setShowUpgradeWarning(true);
    }
    // Otherwise allow adding within their subscription limit
    else {
      setShowAddUserDialog(true);
    }
  };

  const handleUpgradeWarningOk = () => {
    setShowUpgradeWarning(false);
    setShowAddUserDialog(true);
  };

  const handleRoleChange = (role: "admin" | "user") => {
    if (role === "admin") {
      setShowAdminWarning(true);
    }
    setNewUser(prev => ({ ...prev, role }));
  };

  const isFormValid = newUser.name.trim() !== "" && newUser.email.trim() !== "" && newUser.phone.trim() !== "";

  const handleContinueToPayment = async () => {
    if (!isFormValid) {
      toast({
        title: "Please fill in all fields",
        description: "Name, email, and phone number are required to continue.",
        variant: "destructive",
      });
      return;
    }

    const currentUserCount = users.length;
    const maxFreeUsers = BASE_FREE_USERS;
    const isWithinFreeLimit = currentUserCount < maxFreeUsers;

    setProcessingPayment(true);

    try {
      // Send invitation email
      const inviteSubject = "You've been invited to join Wlkthru";
      const inviteHtml = `
        <h2>Welcome to Wlkthru!</h2>
        <p>Hi ${newUser.name},</p>
        <p>You've been invited to join your team's Wlkthru account as a ${newUser.role}.</p>
        <p>Please click the link below to create your account and get started:</p>
        <a href="${window.location.origin}/auth" style="background-color: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Create Account</a>
        <p>If you have any questions, please contact your team administrator.</p>
        <p>Best regards,<br>The Wlkthru Team</p>
      `;

      await sendEmail({
        to: newUser.email,
        subject: inviteSubject,
        html: inviteHtml,
        from: 'noreply@wlkthru.com'
      });

      // If within free limit, just add the user without payment
      if (isWithinFreeLimit) {
        const newUserData: UserData = {
          id: Date.now().toString(),
          name: newUser.name,
          email: newUser.email,
          phone: newUser.phone,
          role: newUser.role,
          joinDate: new Date().toISOString().split('T')[0],
          status: "pending",
          avatar: undefined
        };

        setUsers(prev => [...prev, newUserData]);
        setShowAddUserDialog(false);
        setNewUser({ name: "", email: "", phone: "", countryCode: "+1", role: "user" });

        toast({
          title: "User invited successfully!",
          description: "Invitation sent. The user will receive an email to create their account."
        });
      } else {
        // Need to upgrade - redirect to payment
        const newUserCount = Math.max(
          currentUserCount + 1,
          (subscriptionData?.user_count || BASE_FREE_USERS) + 1
        );

        // Build success/error URLs for PostMessage communication
        // Use VITE_APP_BASE_URL for production or window.location.origin for local dev
        const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
        const successUrl = `${baseUrl}/subscription-success?session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${baseUrl}/subscription-error?message=Payment was canceled`;

        console.log('[UsersPage] Creating checkout with URLs:', { baseUrl, successUrl, cancelUrl });

        // Get current user data for checkout
        const { data: { user } } = await supabase.auth.getUser();

        const data = await apiClient.post<{ url: string }>('/edge/create-checkout', {
          userCount: newUserCount,
          userData: {
            id: user?.id,
            email: user?.email,
            user_metadata: user?.user_metadata
          },
          successUrl,
          cancelUrl
        });

        if (!data?.url) throw new Error('No checkout URL returned');

        console.log('[UsersPage] Got checkout URL:', data.url);

        setShowAddUserDialog(false);
        setNewUser({ name: "", email: "", phone: "", countryCode: "+1", role: "user" });

        // Use unified checkout handler (manages processingPayment internally)
        const { cleanup } = handleCheckout({
          checkoutUrl: data.url,
          onSuccess: async () => {
            console.log('[UsersPage] ✅ Payment successful!');

            toast({
              title: "Payment Successful!",
              description: "User added successfully. Your subscription has been upgraded!",
            });
            setProcessingPayment(false);

            // Refresh subscription data
            try {
              await refreshSubscription();
            } catch (error) {
              console.error('[UsersPage] Error refreshing subscription:', error);
            }
          },
          onError: (errorMessage) => {
            console.log('[UsersPage] ❌ Payment failed:', errorMessage);
            toast({
              title: "Payment Failed",
              description: errorMessage || "Please try again or contact support.",
              variant: "destructive",
            });
            setProcessingPayment(false);
          },
          onClose: () => {
            console.log('[UsersPage] Checkout window closed');
            refreshSubscription();
            setProcessingPayment(false);
          },
        });

        // Store cleanup function
        checkoutCleanupRef.current = cleanup;
        return; // Don't reset processingPayment here, checkout handlers will do it
      }
    } catch (error) {
      console.error('[UsersPage] Error in user invitation process:', error);
      toast({
        title: "Error",
        description: "Failed to send invitation or process request",
        variant: "destructive",
      });
    } finally {
      // Only reset if not waiting for checkout (isWithinFreeLimit path)
      if (isWithinFreeLimit) {
        setProcessingPayment(false);
      }
    }
  };

  const handleEditUser = (user: UserData) => {
    setEditingUser(user);
    // Extract country code from phone number if it exists
    if (user.phone) {
      const match = user.phone.match(/^(\+\d+)/);
      if (match) {
        setEditingUserCountryCode(match[1]);
      } else {
        setEditingUserCountryCode("+1");
      }
    } else {
      setEditingUserCountryCode("+1");
    }
    setShowEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (editingUser) {
      setUsers(prev => prev.map(u => u.id === editingUser.id ? editingUser : u));
      setShowEditDialog(false);
      setEditingUser(null);
      toast({
        title: "User updated",
        description: "User information has been successfully updated."
      });
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <p className="text-muted-foreground">Manage your team members and their access levels</p>
      </div>
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleAddUser} className="flex items-center gap-2 mr-8">
            <Plus className="h-4 w-4" />
            Add User
          </Button>

          {/* Upgrade Warning Alert */}
          <AlertDialog open={showUpgradeWarning} onOpenChange={setShowUpgradeWarning}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {!subscriptionData?.subscribed ? "Subscription Required" : "Upgrade Required"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {!subscriptionData?.subscribed 
                    ? "You need an active subscription to manage users. You'll need to upgrade your account to continue."
                    : "You've reached the limit of your current plan. You'll need to upgrade to add more users to your team."
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleUpgradeWarningOk}>OK</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Current Plan Info */}
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="text-lg font-semibold mb-2">Current Plan</h3>
          {loading ? (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : subscriptionData ? (
            <>
              <div className="flex justify-between items-center mb-2">
                <p className="text-muted-foreground">
                  {subscriptionData.subscribed
                    ? `Wlkthru ${subscriptionData.subscription_tier || 'Pro'} - ${(subscriptionData.user_count || BASE_FREE_USERS)} Users included`
                    : `Free Trial - ${BASE_FREE_USERS} User${BASE_FREE_USERS > 1 ? 's' : ''} included`
                  }
                </p>
                <span className="text-2xl">
                  {subscriptionData.subscribed 
                    ? `$${(subscriptionData.user_count || BASE_FREE_USERS) > 2 ? 150 + (((subscriptionData.user_count || BASE_FREE_USERS) - 2) * 50) : 150}/month`
                    : 'Free'
                  }
                </span>
              </div>
              {/* User availability display */}
              <div className="text-sm text-muted-foreground">
                {(() => {
                  const currentUserCount = users.length;
                  const maxUsers = subscriptionData?.subscribed
                    ? (subscriptionData.user_count || BASE_FREE_USERS)
                    : BASE_FREE_USERS;
                  return `${currentUserCount}/${maxUsers} users available`;
                })()}
              </div>
            </>
          ) : (
            <div className="text-muted-foreground">
              <p>Unable to load subscription information</p>
            </div>
          )}
        </Card>

        {/* Users List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Team Members</h2>
          {users.map((user) => (
            <Card 
              key={user.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleEditUser(user)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback className="bg-primary/10">
                        {user.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold">
                        {user.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {user.email}
                      </p>
                      {user.phone && (
                        <p className="text-sm text-muted-foreground">
                          {user.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary" 
                      className={
                        user.role === "admin" 
                          ? "bg-blue-100 text-blue-800 border-blue-200" 
                          : "bg-green-100 text-green-800 border-green-200"
                      }
                    >
                      {user.role}
                    </Badge>
                    <Edit className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter user's name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter user's email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <PhoneInput
                id="phone"
                value={newUser.phone}
                onChange={(value) => setNewUser(prev => ({ ...prev, phone: value }))}
                countryCode={newUser.countryCode}
                onCountryCodeChange={(code) => setNewUser(prev => ({ ...prev, countryCode: code }))}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={newUser.role} onValueChange={handleRoleChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleContinueToPayment} 
              className="bg-primary"
              disabled={!isFormValid || isEmailLoading}
            >
              {isEmailLoading 
                ? "Sending Invitation..." 
                : users.length < 2 
                  ? "Add User" 
                  : "Continue to Payment"
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Profile Picture</Label>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={editingUser.avatar} alt={editingUser.name} />
                    <AvatarFallback className="bg-primary/10">
                      {editingUser.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Picture
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG up to 2MB</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser(prev => prev ? { ...prev, name: e.target.value } : null)}
                  placeholder="Enter user's name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : null)}
                  placeholder="Enter user's email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone Number</Label>
                <PhoneInput
                  id="edit-phone"
                  value={editingUser.phone || ""}
                  onChange={(value) => setEditingUser(prev => prev ? { ...prev, phone: value } : null)}
                  countryCode={editingUserCountryCode}
                  onCountryCodeChange={setEditingUserCountryCode}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select 
                  value={editingUser.role} 
                  onValueChange={(role: "admin" | "user") => 
                    setEditingUser(prev => prev ? { ...prev, role } : null)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Warning Dialog */}
      <AlertDialog open={showAdminWarning} onOpenChange={setShowAdminWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin Role Warning</AlertDialogTitle>
            <AlertDialogDescription>
              There can only be one admin at a time. If you assign this user as admin, 
              the current admin will lose their admin privileges and become a regular user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setNewUser(prev => ({ ...prev, role: "user" }));
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
